import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Mark, DeviceMock } from "../Landing/Landing";
import "../Landing/Landing.css";
import "./Explore.css";

// A low-commitment "explore before you decide" page: how simple setup is, and
// everything the product does — no purchase required. The trial/sign-in CTAs live
// only at the very bottom, after the exploration.

// [icon, title, benefit] — features + benefits, tightened for flow.
const FEATURES = [
  ["📋", "An always-on message board",
    "Every message stays on screen at once, while photos rotate in their own panel — so a note is never more than a glance away."],
  ["⏰", "Reminders that glow, then chime",
    "A reminder gently glows as its moment approaches, then sounds a soft chime right on time — a kind, unmissable nudge."],
  ["📅", "Schedule any event",
    "Choose when a note appears, how long it stays, and whether it repeats daily or weekly."],
  ["🔔", "Inactivity nudge",
    "If the board goes quiet for a while, we gently remind the Care Circle to post something new."],
  ["🌙", "Quiet hours",
    "The screen rests overnight on its own, on a schedule you set for each resident."],
  ["📦", "The screen is included",
    "It arrives configured and ready — just plug it in. No hunting for hardware."],
  ["😊", "Nothing for your loved one to learn",
    "No logins, taps, or menus, ever. It simply shows your messages and photos."],
  ["📶", "Works without Wi-Fi",
    "Choose a model with built-in cellular for rooms where Wi-Fi isn't available or allowed."],
  ["🛡️", "Always secure, always current",
    "HIPAA-compliant handling of messages and contacts, with security updates applied automatically — nothing to maintain."],
  ["👨‍👩‍👧", "The whole Care Circle, together",
    "Invite unlimited family and friends to one shared board — everyone can post."],
  ["🔒", "Private and separate",
    "Each Care Circle is fully isolated on enterprise-grade cloud infrastructure."],
  ["🖥️", "As many screens as you like",
    "Add displays in the kitchen, bedroom, and living room — every one shows the same messages, reminders, and photos."],
  ["👥", "More than one loved one",
    "Keep a personalized board for each resident, all from the same app."],
];

export default function Explore() {
  const auth = useSelector((state) => state.auth);
  const signedIn = auth && auth.tenantId;
  const startHref = signedIn ? "/home" : "/start";
  const startLabel = signedIn ? "Go to your board" : "Start your 30-day free trial";

  return (
    <div className="lp">
      <div className="lp-wrap">
        <nav className="lp-nav">
          <Link to="/" className="lp-brand"><Mark /> Mary's Bulletin Board</Link>
          <div className="lp-nav-actions">
            {signedIn
              ? <Link to="/home" className="lp-btn lp-btn-primary">Go to your board</Link>
              : <Link to="/" className="lp-btn lp-btn-ghost">← Back to home</Link>}
          </div>
        </nav>
      </div>

      <header className="lp-hero">
        <div className="lp-wrap">
          <span className="lp-eyebrow">Take a look around</span>
          <h1>See how simple it is<br /><span className="grad">before you decide</span></h1>
          <p className="lp-sub" style={{ maxWidth: "44ch" }}>
            No sign-up needed to look. Here's exactly what setup involves, and everything
            the board can do for your family.
          </p>
          <div className="lp-hero-art" style={{ marginTop: "2.2em" }}>
            <DeviceMock />
          </div>
        </div>
      </header>

      <section className="lp-section" id="setup">
        <div className="lp-wrap">
          <h2>Up and running in minutes</h2>
          <p className="lp-lead">We handle the technical part before it even reaches the room.</p>
          <div className="lp-steps">
            <div className="lp-step">
              <div className="num">1</div>
              <h3>It arrives ready</h3>
              <p>The screen ships already configured for your Care Circle — signed in, locked to the board, and set up for your network. Nothing to install.</p>
            </div>
            <div className="lp-step">
              <div className="num">2</div>
              <h3>Plug it in</h3>
              <p>Set it on a shelf or table and power it on. It turns straight to your loved one's board and reconnects by itself after any power blip.</p>
            </div>
            <div className="lp-step">
              <div className="num">3</div>
              <h3>Send a little love</h3>
              <p>Post messages, photos, and reminders from your phone. They appear on the screen in seconds — no app or login for your loved one.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-section" id="features">
        <div className="lp-wrap">
          <h2>Everything it does</h2>
          <p className="lp-lead">Thoughtful features, all designed to keep a loved one close.</p>
          <div className="feat-wrap">
            <table className="feat-table">
              <tbody>
                {FEATURES.map(([ic, title, benefit]) => (
                  <tr key={title}>
                    <th scope="row"><span className="feat-ic" aria-hidden="true">{ic}</span>{title}</th>
                    <td>{benefit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="lp-final">
        <div className="lp-wrap">
          <h2>Explored enough? Try it free</h2>
          <p className="lp-lead" style={{ color: "rgba(255,255,255,0.9)" }}>
            30 days free, cancel anytime — the screen ships to their door.
          </p>
          <div className="lp-cta-row">
            <Link to={startHref} className="lp-btn lp-btn-primary lp-btn-lg">{startLabel}</Link>
            {!signedIn && <Link to="/signin" className="lp-btn lp-btn-ghost lp-btn-lg">Sign in</Link>}
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-wrap">
          © Mary's Bulletin Board · A private, secure way for families to stay close to a loved one in care.
          {" · "}
          <Link to="/terms" style={{ color: "#9db3ff" }}>Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
